import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Star, Trash2, Plus, Check } from "lucide-react";

type T = {
  id: string;
  author: string;
  author_role: string | null;
  quote: string;
  rating: number;
  sort_order: number;
  active: boolean;
  approved: boolean;
  submitted_via: string;
};

export const Route = createFileRoute("/admin/testimonials")({ component: AdminTestimonials });

function AdminTestimonials() {
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.from("testimonials").select("*").order("approved").order("sort_order").order("created_at", { ascending: false });
    if (error) toast.error(error.message); else setItems((data as T[]) || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    const { error } = await supabase.from("testimonials").insert({ author: "New customer", quote: "Write the quote here…", rating: 5, sort_order: items.length, approved: true, active: true, submitted_via: "admin" });
    if (error) toast.error(error.message); else load();
  };

  const update = (id: string, patch: Partial<T>) => setItems(p => p.map(t => t.id === id ? { ...t, ...patch } : t));

  const save = async (t: T) => {
    const { error } = await supabase.from("testimonials").update({
      author: t.author, author_role: t.author_role, quote: t.quote,
      rating: Number(t.rating), sort_order: Number(t.sort_order), active: t.active, approved: t.approved,
    }).eq("id", t.id);
    if (error) toast.error(error.message); else toast.success("Saved");
  };

  const approve = async (t: T) => {
    const { error } = await supabase.from("testimonials").update({ approved: true, active: true }).eq("id", t.id);
    if (error) toast.error(error.message); else { toast.success("Approved & published"); load(); }
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this testimonial?")) return;
    const { error } = await supabase.from("testimonials").delete().eq("id", id);
    if (error) toast.error(error.message); else load();
  };

  const pendingCount = items.filter(t => !t.approved).length;

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl text-espresso">Testimonials</h1>
        <Button onClick={add} className="bg-primary"><Plus className="mr-2 h-4 w-4" />Add</Button>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">
        Shown on the About page once approved. Customers can submit reviews at <code className="rounded bg-muted px-1">/review</code>.
      </p>
      {pendingCount > 0 && (
        <div className="mt-4 rounded-md border border-accent/40 bg-accent/10 p-3 text-sm">
          <strong>{pendingCount}</strong> review{pendingCount > 1 ? "s" : ""} pending your approval.
        </div>
      )}

      {loading ? (
        <p className="mt-6 text-muted-foreground">Loading…</p>
      ) : items.length === 0 ? (
        <Card className="mt-6 p-8 text-center text-muted-foreground">No testimonials yet. Click <strong>Add</strong> to create one.</Card>
      ) : (
        <div className="mt-6 space-y-4">
          {items.map(t => (
            <Card key={t.id} className={`space-y-3 p-5 ${!t.approved ? "border-accent/60 ring-1 ring-accent/30" : ""}`}>
              <div className="flex flex-wrap items-center gap-2">
                {!t.approved && <Badge className="bg-accent text-espresso">Pending approval</Badge>}
                {t.submitted_via === "public" && <Badge variant="outline">Submitted by customer</Badge>}
                {t.approved && t.active && <Badge variant="secondary">Live on site</Badge>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Author</Label><Input value={t.author} onChange={e => update(t.id, { author: e.target.value })} /></div>
                <div className="space-y-1"><Label>Role / trip type (optional)</Label><Input value={t.author_role || ""} onChange={e => update(t.id, { author_role: e.target.value })} placeholder="Visited from NYC" /></div>
              </div>
              <div className="space-y-1"><Label>Quote</Label><Textarea rows={3} value={t.quote} onChange={e => update(t.id, { quote: e.target.value })} /></div>
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <Label className="text-sm">Rating</Label>
                  <Input type="number" min="1" max="5" className="w-20" value={t.rating} onChange={e => update(t.id, { rating: Number(e.target.value) })} />
                  <div className="flex">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`h-4 w-4 ${i < t.rating ? "fill-accent text-accent" : "text-muted-foreground/30"}`} />)}</div>
                </div>
                <div className="flex items-center gap-2"><Label className="text-sm">Order</Label><Input type="number" className="w-20" value={t.sort_order} onChange={e => update(t.id, { sort_order: Number(e.target.value) })} /></div>
                <div className="flex items-center gap-2"><Label className="text-sm">Active</Label><Switch checked={t.active} onCheckedChange={v => update(t.id, { active: v })} /></div>
                <div className="ml-auto flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => remove(t.id)}><Trash2 className="h-4 w-4" /></Button>
                  {!t.approved && <Button size="sm" className="bg-accent text-espresso hover:bg-accent/90" onClick={() => approve(t)}><Check className="mr-1 h-4 w-4" />Approve</Button>}
                  <Button size="sm" className="bg-primary" onClick={() => save(t)}>Save</Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
